import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotesIcon from '@mui/icons-material/Notes';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { uploadImage, getUploadedMediaRecord } from '../../http/uploads';

const MAX_IMAGES = 3;

let blockKeyCounter = 0;
const nextBlockKey = () => `block-${++blockKeyCounter}`;

const makeTextBlock = () => ({ key: nextBlockKey(), type: 'TEXT', text: '' });
const makeImageBlock = () => ({
  key: nextBlockKey(),
  type: 'IMAGE',
  imageUrl: '',
  uploading: false,
});

const PatchUpdateForm = ({ open, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState({
    name: '',
    buildName: '',
    appStoreUrl: '',
    playStoreUrl: '',
    sendPush: false,
    sendDate: null,
  });
  const [blocks, setBlocks] = useState([makeTextBlock()]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRefs = useRef({});

  // Editing is only possible while the record hasn't published yet
  const isDisabled = initialData ? initialData.status !== 'PENDING' : false;

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        buildName: initialData.buildName || '',
        appStoreUrl: initialData.appStoreUrl || '',
        playStoreUrl: initialData.playStoreUrl || '',
        sendPush: Boolean(initialData.sendPush),
        sendDate: initialData.sendDate ? dayjs(initialData.sendDate) : null,
      });
      setBlocks(
        (initialData.blocks || []).length > 0
          ? initialData.blocks.map((block) => ({
              key: nextBlockKey(),
              type: block.type,
              text: block.text || '',
              imageUrl: block.imageUrl || '',
              uploading: false,
            }))
          : [makeTextBlock()]
      );
    } else {
      setFormData({
        name: '',
        buildName: '',
        appStoreUrl: '',
        playStoreUrl: '',
        sendPush: false,
        sendDate: null,
      });
      setBlocks([makeTextBlock()]);
    }
    setError('');
  }, [initialData, open]);

  const imageCount = blocks.filter((block) => block.type === 'IMAGE').length;

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const updateBlock = (key, changes) => {
    setBlocks((current) =>
      current.map((block) =>
        block.key === key ? { ...block, ...changes } : block
      )
    );
  };

  const moveBlock = (index, direction) => {
    setBlocks((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeBlock = (key) => {
    setBlocks((current) => current.filter((block) => block.key !== key));
  };

  const handleImageFileSelected = async (key, file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setError('Images must be JPEG or PNG');
      return;
    }
    setError('');
    updateBlock(key, { uploading: true });
    try {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('folder', 'patch-updates');
      const res = await uploadImage(payload);
      const media = getUploadedMediaRecord(res);
      if (!media?.url) {
        throw new Error('Upload did not return a media url');
      }
      updateBlock(key, { imageUrl: media.url, uploading: false });
    } catch (uploadError) {
      console.error('Error uploading patch update image:', uploadError);
      updateBlock(key, { uploading: false });
      setError('Image upload failed — please try again');
    }
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Name is required';
    if (!formData.buildName.trim()) return 'Build name is required';
    if (blocks.length === 0) return 'Add at least one content block';
    if (blocks.some((block) => block.type === 'TEXT' && !block.text.trim())) {
      return 'Text blocks cannot be empty';
    }
    if (blocks.some((block) => block.type === 'IMAGE' && block.uploading)) {
      return 'Wait for image uploads to finish';
    }
    if (blocks.some((block) => block.type === 'IMAGE' && !block.imageUrl)) {
      return 'Every image block needs an uploaded image (or remove it)';
    }
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      name: formData.name.trim(),
      buildName: formData.buildName.trim(),
      blocks: blocks.map((block) =>
        block.type === 'TEXT'
          ? { type: 'TEXT', text: block.text.trim() }
          : { type: 'IMAGE', imageUrl: block.imageUrl }
      ),
      appStoreUrl: formData.appStoreUrl.trim(),
      playStoreUrl: formData.playStoreUrl.trim(),
      sendPush: formData.sendPush,
      sendDate: formData.sendDate
        ? formData.sendDate.format('YYYY-MM-DDTHH:mm:ssZ')
        : null,
    };

    setSubmitting(true);
    const success = await onSubmit(payload);
    setSubmitting(false);
    if (success) {
      onClose();
    }
  };

  const renderBlock = (block, index) => (
    <Paper
      key={block.key}
      variant="outlined"
      sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <IconButton
          size="small"
          onClick={() => moveBlock(index, -1)}
          disabled={isDisabled || index === 0}
          aria-label="Move block up"
        >
          <ArrowUpwardIcon fontSize="inherit" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => moveBlock(index, 1)}
          disabled={isDisabled || index === blocks.length - 1}
          aria-label="Move block down"
        >
          <ArrowDownwardIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1 }}>
        {block.type === 'TEXT' ? (
          <TextField
            value={block.text}
            onChange={(event) =>
              updateBlock(block.key, { text: event.target.value })
            }
            placeholder="Patch notes text…"
            fullWidth
            multiline
            minRows={2}
            disabled={isDisabled}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {block.uploading ? (
              <Box
                sx={{
                  width: 120,
                  height: 68,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed #bbb',
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={22} />
              </Box>
            ) : block.imageUrl ? (
              <img
                src={block.imageUrl}
                alt="Patch update"
                style={{
                  width: 120,
                  height: 68,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #ddd',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 120,
                  height: 68,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed #bbb',
                  borderRadius: 1,
                  color: '#999',
                }}
              >
                <ImageOutlinedIcon />
              </Box>
            )}
            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={isDisabled || block.uploading}
            >
              {block.imageUrl ? 'Replace Image' : 'Upload Image'}
              <input
                hidden
                type="file"
                accept="image/png, image/jpeg"
                ref={(element) => {
                  fileInputRefs.current[block.key] = element;
                }}
                onChange={(event) => {
                  handleImageFileSelected(block.key, event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </Button>
          </Box>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={() => removeBlock(block.key)}
        disabled={isDisabled}
        aria-label="Remove block"
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Edit Patch Update' : 'Add Patch Update'}
      </DialogTitle>
      <DialogContent>
        {isDisabled && (
          <Typography color="error" sx={{ mb: 2 }}>
            {initialData?.status === 'SENT'
              ? 'This patch update has already been published and cannot be modified'
              : 'This patch update has been canceled and cannot be modified'}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            placeholder='e.g. "The Group Duets Update"'
            fullWidth
            required
            disabled={isDisabled}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />
          <TextField
            label="Build Name"
            value={formData.buildName}
            onChange={handleChange('buildName')}
            placeholder="e.g. v2.4.0 (build 187)"
            fullWidth
            required
            disabled={isDisabled}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Patch Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add text and image blocks in the order they should appear in the
              app (up to {MAX_IMAGES} images).
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {blocks.map(renderBlock)}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                startIcon={<NotesIcon />}
                onClick={() => setBlocks((current) => [...current, makeTextBlock()])}
                disabled={isDisabled}
              >
                Add Text
              </Button>
              <Tooltip
                title={
                  imageCount >= MAX_IMAGES
                    ? `Maximum of ${MAX_IMAGES} images per patch update`
                    : ''
                }
              >
                <span>
                  <Button
                    size="small"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() =>
                      setBlocks((current) => [...current, makeImageBlock()])
                    }
                    disabled={isDisabled || imageCount >= MAX_IMAGES}
                  >
                    Add Image
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            label="App Store URL (optional)"
            value={formData.appStoreUrl}
            onChange={handleChange('appStoreUrl')}
            placeholder="https://apps.apple.com/app/…"
            fullWidth
            disabled={isDisabled}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />
          <TextField
            label="Google Play URL (optional)"
            value={formData.playStoreUrl}
            onChange={handleChange('playStoreUrl')}
            placeholder="https://play.google.com/store/apps/details?id=…"
            fullWidth
            disabled={isDisabled}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.sendPush}
                onChange={(event) =>
                  setFormData({ ...formData, sendPush: event.target.checked })
                }
                disabled={isDisabled}
              />
            }
            label="Also send a push notification when this publishes"
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DateTimePicker
                label="Send Date & Time (optional)"
                value={formData.sendDate}
                onChange={(date) => setFormData({ ...formData, sendDate: date })}
                ampm={true}
                format="MM/DD/YYYY hh:mm A"
                minDateTime={dayjs()}
                disablePast
                disabled={isDisabled}
                sx={{
                  flex: 1,
                  '& .MuiInputBase-input': { color: 'black !important' },
                }}
              />
              {formData.sendDate && !isDisabled && (
                <Button
                  size="small"
                  onClick={() => setFormData({ ...formData, sendDate: null })}
                >
                  Clear
                </Button>
              )}
            </Box>
          </LocalizationProvider>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Leave the date empty to publish immediately.
          </Typography>

          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isDisabled || submitting}
        >
          {submitting
            ? 'Saving…'
            : initialData
              ? 'Update'
              : formData.sendDate
                ? 'Schedule'
                : 'Publish Now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatchUpdateForm;
